import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import ajaxRole from "../../util/accounting/remote/ajaxRole";
import ajaxUser from "../../util/accounting/remote/ajaxUser";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const APPLIES_TO = [
  { value: "journal_entry", label: "Journal Entry" },
  { value: "voucher", label: "Voucher" },
  { value: "invoice", label: "Invoice" },
  { value: "payroll_run", label: "Payroll Run" },
];

const ApprovalWorkflows = () => {
  const [workflows, setWorkflows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [levelFor, setLevelFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", applies_to: "journal_entry", min_amount: "", max_amount: "", description: "" });
  const [level, setLevel] = useState({ level_order: 1, approver_type: "role", approver_id: "", label: "", any_one: "1" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [w, r, u] = await Promise.all([
      ajaxAccounting.listWorkflows({}),
      ajaxRole.fetchRoleList({}),
      ajaxUser.fetchUserList({ page: 1 }),
    ]);
    if (w.status === "OK") setWorkflows(Array.isArray(w.details) ? w.details : w.details?.list || []);
    if (r.status === "OK") setRoles(r.details || []);
    if (u.status === "OK") setUsers(u.details?.list || u.details || []);
    setLoading(false);
  };

  const roleName = (id) => { const r = roles.find((x) => String(x.role_id ?? x.id) === String(id)); return r ? r.role_name || r.name : `Role #${id}`; };
  const userName = (id) => { const u = users.find((x) => String(x.id) === String(id)); return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email : `User #${id}`; };

  const handleCreate = async () => {
    if (!form.name) { toast.error("Workflow name is required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.createWorkflow({
      name: form.name, applies_to: form.applies_to,
      min_amount: form.min_amount ? parseFloat(form.min_amount) : 0,
      max_amount: form.max_amount ? parseFloat(form.max_amount) : 0,
      description: form.description,
    });
    if (res.status === "OK") { toast.success("Workflow created!"); setShowCreate(false); setForm({ name: "", applies_to: "journal_entry", min_amount: "", max_amount: "", description: "" }); fetchAll(); }
    else toast.error(res.message || "Failed to create workflow");
    setSaving(false);
  };

  const openAddLevel = (wf) => {
    const nextOrder = (wf.levels?.length || 0) + 1;
    setLevel({ level_order: nextOrder, approver_type: "role", approver_id: "", label: "", any_one: "1" });
    setLevelFor(wf);
  };

  const handleAddLevel = async () => {
    if (!level.approver_id) { toast.error("Select an approver."); return; }
    setSaving(true);
    const res = await ajaxAccounting.addWorkflowLevel({
      workflow_id: levelFor.id,
      level_order: parseInt(level.level_order),
      approver_type: level.approver_type,
      approver_id: parseInt(level.approver_id),
      label: level.label || `Level ${level.level_order}`,
      any_one: parseInt(level.any_one),
    });
    if (res.status === "OK") { toast.success("Level added!"); setLevelFor(null); fetchAll(); }
    else toast.error(res.message || "Failed to add level");
    setSaving(false);
  };

  const approverOptions = level.approver_type === "role"
    ? roles.map((r) => ({ value: String(r.role_id ?? r.id), label: r.role_name || r.name }))
    : users.map((u) => ({ value: String(u.id), label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email }));

  return (
    <AppContainer
      title="Approval Workflows"
      previousPageTitle="Ledger Approvals"
      description="Define multi-level approval workflows for ledger entries. Each workflow applies to an entity type and an optional amount range; add ordered levels with a user or role approver. The final level's approval posts the entry."
      actions={[{ label: "New Workflow", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowCreate(true) }]}
    >
      <Toaster />
      {loading ? (
        <div className="text-center py-5 tx-muted">Loading…</div>
      ) : workflows.length === 0 ? (
        <div className="card custom-card p-5 text-center tx-muted">
          No workflows yet. Create one to route entries through approval before they post.
        </div>
      ) : (
        <div className="row">
          {workflows.map((wf) => (
            <div key={wf.id} className="col-lg-6 mb-3">
              <div className="card custom-card p-3 h-100">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 className="fw-semibold mb-1">{wf.name}</h6>
                    <span className="badge bg-light text-dark text-capitalize me-1">{(wf.applies_to || "").replace("_", " ")}</span>
                    {(wf.min_amount > 0 || wf.max_amount > 0) && (
                      <span className="tx-11 tx-muted">
                        {functions.formatCurrency(wf.min_amount)} – {wf.max_amount > 0 ? functions.formatCurrency(wf.max_amount) : "∞"}
                      </span>
                    )}
                  </div>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => openAddLevel(wf)}><i className="fa-solid fa-plus me-1"></i>Level</button>
                </div>
                {wf.description && <p className="tx-12 tx-muted mb-2">{wf.description}</p>}
                <table className="table table-sm mb-0">
                  <tbody>
                    {(wf.levels || []).sort((a, b) => a.level_order - b.level_order).map((lv, i) => (
                      <tr key={i}>
                        <td style={{ width: "40px" }}><span className="badge bg-primary">{lv.level_order}</span></td>
                        <td className="tx-12">{lv.label}</td>
                        <td className="tx-12 text-end">
                          {lv.approver_type === "role" ? <i className="fa-solid fa-users-gear me-1"></i> : <i className="fa-solid fa-user me-1"></i>}
                          {lv.approver_type === "role" ? roleName(lv.approver_id) : userName(lv.approver_id)}
                        </td>
                      </tr>
                    ))}
                    {(!wf.levels || wf.levels.length === 0) && <tr><td colSpan={3} className="tx-muted tx-12 text-center">No levels — add at least one.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create workflow modal */}
      {showCreate && (
        <AppModal
          title="New Approval Workflow"
          size="lg"
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Saving..." : "Create Workflow"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Journal approval over 1M" />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Applies To</label>
              <Select2 options={APPLIES_TO} value={form.applies_to} onChange={(v) => setForm({ ...form, applies_to: v })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Min Amount</label>
              <input className="form-control" type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} placeholder="0" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Max Amount (0 = no limit)</label>
              <input className="form-control" type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} placeholder="0" />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}

      {/* Add level modal */}
      {levelFor && (
        <AppModal
          title={<>Add Level — {levelFor.name}</>}
          onClose={() => setLevelFor(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setLevelFor(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddLevel} disabled={saving}>{saving ? "Saving..." : "Add Level"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Order</label>
              <input className="form-control" type="number" value={level.level_order} onChange={(e) => setLevel({ ...level, level_order: e.target.value })} />
            </div>
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Label</label>
              <input className="form-control" value={level.label} onChange={(e) => setLevel({ ...level, label: e.target.value })} placeholder="e.g. Manager approval" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Approver Type</label>
              <Select2 options={[{ value: "role", label: "Role" }, { value: "user", label: "Specific User" }]} value={level.approver_type} onChange={(v) => setLevel({ ...level, approver_type: v, approver_id: "" })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Approver</label>
              <Select2 options={approverOptions} value={level.approver_id} onChange={(v) => setLevel({ ...level, approver_id: v })} placeholder="Select approver" />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default ApprovalWorkflows;
